{
  "hero": {
    "eyebrow": "Projet",
    "title": "Construire un robot de surveillance de surface semi-autonome assisté par radar.",
    "lead": "AquaWorks est un projet d'ingénierie mené par des étudiants, développé par des étudiants de l'Université de la Colombie-Britannique. Il crée une plateforme réutilisable d'observation de la qualité de l'eau liée au lieu, en milieux d'eau stagnante contrôlée — et, avec approbation, sur des sites à faible risque du campus de Vancouver."
  },
  "purpose": {
    "title": "Objectif général",
    "body": "Rendre les conditions de qualité de l'eau plus faciles à observer, comprendre et communiquer. Chaque phase s'appuie sur la précédente : adaptabilité, autonomie, valeur des données et potentiel de recherche."
  },
  "phases": {
    "title": "Feuille de route",
    "items": {
      "phase1": {
        "label": "Phase 1",
        "title": "Surveillance en eau stagnante assistée par radar",
        "body": "Pilotage à distance principal avec appui semi-autonome pour suivi de trajectoire à basse vitesse et distance aux obstacles. Capteurs clés, journaux CSV, cartes de trajet et au moins une visualisation spatiale. Focus de la demande AMS SPF actuelle.",
        "window": "Sept.–déc. 2026 · rapport janv. 2027"
      },
      "phase2": {
        "label": "Phase 2",
        "title": "Suivi linéaire près du rivage",
        "body": "Vers des trajets riverains approuvés (par ex. autour d'un exutoire). Améliorer anti-échouement ou structure semi-amphibie, GPS, stabilité de navigation et portée radio — sous permission et revue de sécurité."
      },
      "phase3": {
        "label": "Phase 3",
        "title": "Surveillance autonome par zone",
        "body": "Trajets en grille ou points chauds sur une zone riveraine, capacité amphibie renforcée et appui à l'échantillonnage ciblé pour analyses de laboratoire ultérieures."
      }
    }
  },
  "objectives": {
    "title": "Objectifs de la Phase 1",
    "items": [
      { "title": "Robot flottant modulaire stable", "body": "Un prototype intégré avec flottabilité, étanchéité, propulsion, charge utile et récupération à terre." },
      { "title": "Système de capteurs modulaire", "body": "Température, pH, turbidité, conductivité et oxygène dissous avec étalonnage ou vérification documentés." },
      { "title": "Contrôle, communication, journalisation", "body": "Pilotage fiable, stockage sans fil ou local, horodatage et références spatiales synchronisés, distance radar dans le journal." },
      { "title": "Flux données et visualisation", "body": "Export CSV, cartes de trajet, tendances et au moins une visualisation spatiale." },
      { "title": "Validation par étapes", "body": "Tests au banc jusqu'à l'intégration en eau contrôlée ; tests en plan d'eau campus seulement avec permission." },
      { "title": "Archive complète", "body": "CAO, câblage, logiciel, étalonnage, sécurité, maintenance et rapport final d'ici le 31 janvier 2027." }
    ]
  },
  "system": {
    "title": "Cinq sous-systèmes coordonnés",
    "lead": "Plateforme flottante mécanique, électrique et alimentation, capteurs de qualité de l'eau, propulsion et contrôle, journalisation et visualisation.",
    "workflow": "Planification → vérifications → déploiement → opération → mesure → horodatage et lieu → stockage ou transmission → récupération → validation → visualisation.",
    "items": [
      { "title": "Plateforme mécanique", "body": "Coque ou multi-flotteurs, zone de charge modulaire, enceinte étanche, supports, points de récupération." },
      { "title": "Électrique et alimentation", "body": "Batterie rechargeable, régulation, drivers, microcontrôleur, câblage protégé, isolation d'urgence." },
      { "title": "Capteurs", "body": "Indicateurs interchangeables pour conditions générales — pas de confirmation laboratoire de contaminants spécifiques." },
      { "title": "Propulsion et contrôle", "body": "Mode distant principal ; radar pour awareness. Override manuel toujours disponible." },
      { "title": "Données et visualisation", "body": "Conserver le brut ; produire cartes et courbes avec hypothèses claires et lacunes signalées." }
    ]
  },
  "scope": {
    "title": "Périmètre Phase 1",
    "body": "Concevoir, construire, intégrer et valider un petit robot modulaire pour opération en eau stagnante contrôlée du 1er septembre au 31 décembre 2026, rapport final d'ici le 31 janvier 2027.",
    "sourceTitle": "Faits publics du projet",
    "sourceBody": "Demande AMS Sustainability Projects Fund. Fenêtre prévue : 1 sept.–31 déc. 2026. Rapport final à l'AMS Sustainability Office d'ici le 31 janv. 2027. Les montants exacts ne sont pas publiés sur ce site."
  },
  "outOfScope": {
    "title": "Hors périmètre Phase 1",
    "items": [
      "Navigation pleinement autonome ou planification de mission complexe",
      "Eau libre, littoral, fort courant ou météo dangereuse",
      "Mobilité semi-amphibie / anti-échouement comme livrable requis",
      "Échantillonnage physique automatisé ou détection de contaminants de niveau laboratoire",
      "Surveillance réglementaire, eau potable ou santé publique",
      "Déploiement long sans surveillance ou tests sans approbation de site"
    ]
  },
  "principles": {
    "title": "Principes de conception",
    "items": [
      { "title": "Modularité", "body": "Capteurs et composants majeurs remplaçables sans reconstruire toute la plateforme." },
      { "title": "Réparabilité", "body": "Inspecter, entretenir, remplacer et dépanner avec une documentation claire." },
      { "title": "Sécurité d'abord", "body": "Basse vitesse, supervision visuelle, override manuel et récupération fiable avant l'autonomie avancée." },
      { "title": "Traçabilité des données", "body": "Lectures brutes, étalonnage, traitements et conditions d'essai documentés." }
    ]
  },
  "timeline": {
    "title": "Calendrier prévu Phase 1",
    "items": {
      "sep": { "label": "Sept. 2026", "title": "Mise en place", "body": "Exigences, CAO, architecture électrique, plan capteurs, procédures de sécurité et vérifications." },
      "oct": { "label": "Oct. 2026", "title": "Construction des sous-systèmes", "body": "Fabriquer la plateforme ; tester l'électronique ; modules logiciels capteurs, contrôle et journal." },
      "nov": { "label": "Nov. 2026", "title": "Intégration et eau contrôlée", "body": "Flottabilité, étanchéité, propulsion, étalonnage et premiers jeux de données synchronisés." },
      "dec": { "label": "Déc. 2026", "title": "Validation système", "body": "Événement de test en eau contrôlée ; démo campus optionnelle si permission et conditions le permettent." },
      "jan": { "label": "Janv. 2027", "title": "Rapport et passation", "body": "Rapport final, archive technique, guide de maintenance et recommandations." }
    }
  },
  "disclaimer": {
    "title": "Limites assumées",
    "body": "AquaWorks ne remplace pas l'évaluation environnementale professionnelle, la surveillance réglementaire ni les essais de laboratoire certifiés. C'est une plateforme étudiante d'apprentissage spatial, d'observation préliminaire et de fondations techniques réutilisables."
  },
  "cta": {
    "primary": "Explorer la science",
    "secondary": "Rencontrer les équipes"
  }
}
